"use client"

import { useState, useEffect } from 'react'
import { Monitor, Smartphone } from 'lucide-react'

interface MobileBlockProps {
  children: React.ReactNode
}

export default function MobileBlock({ children }: MobileBlockProps) {
  const [windowWidth, setWindowWidth] = useState<number>(0)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
    setWindowWidth(window.innerWidth)

    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Don't render anything on server side to prevent hydration mismatch
  if (!isClient) {
    return null
  }

  // Show mobile block message if width is less than 1050px
  if (windowWidth < 1050) {
    return (
      <div className="fixed inset-0 min-h-screen w-full bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center px-4 sm:px-6 lg:px-8">
        
        {/* Background Effect */}
        <div className="absolute inset-0 w-full overflow-hidden opacity-50">
          {/* Wave Background */}
          <div className="absolute inset-0 mt-0 blur-[10px] animate-slideUp opacity-1">
            <div id="wave" className="bg-wave"></div>
          </div>
        </div>
        
        {/* Grain Texture Overlay */}
        <div 
          className="absolute inset-0" 
          style={{
            backgroundImage: 'url(/grain.png)',
            backgroundSize: '100px 100px',
            backgroundRepeat: 'repeat',
            backgroundBlendMode: 'overlay',
            backgroundPosition: 'left top',
            mixBlendMode: 'overlay',
            opacity: 1
          }}
        />

        <div className="w-full max-w-md sm:max-w-lg p-6 sm:p-8 space-y-8 rounded-xl relative z-10 mx-4"
        style={{
          border: 'none',
          padding: '60px',
        }}>
          <div className="flex flex-col items-center text-center">
            <img 
              alt="Migma.ai Logo" 
              loading="lazy" 
              width="72" 
              height="72" 
              decoding="async" 
              className="h-16 w-16 sm:h-20 sm:w-20 mb-6" 
              src="/migma-icon.svg" 
              style={{ color: 'transparent' }}
            />
            
            <div className="flex items-center justify-center gap-4 mb-6">
              <Smartphone className="w-8 h-8 text-red-400" />
              <div className="text-red-400 text-2xl">→</div>
              <Monitor className="w-8 h-8 text-green-400" />
            </div>
            
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              Mobile Viewing Not Available
            </h2>
            <p className="text-sm sm:text-base text-zinc-400 leading-relaxed max-w-sm mb-4">
              This presentation is optimized for desktop viewing. Please switch to a PC or laptop with a screen width of at least 1050px for the best experience.
            </p>
            <div className="text-xs text-zinc-500 font-mono">
              Current width: {windowWidth}px | Required: 1050px+
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Render children if width is sufficient
  return <>{children}</>
}
